import { Inline, Rows } from 'base/ui/layout';
import { DividerLine, PanelLayout } from 'base/ui/panel';
import { ToastQueue } from 'base/ui/toast';
import React from 'react';
import { SidebarNavigation } from './routes';

import { AnalyticsService } from 'base/analytics';
import { generateNeutralShades, getNeutralHex } from 'base/styleguide/color';
import { assertPaletteName } from 'base/styleguide/concept';
import {
  CompiledChromaticColor,
  CompiledNeutralColor,
} from 'base/styleguide/types';
import { Button } from 'base/ui/button';
import { Icon } from 'base/ui/icon';
import { IconNoColor, IconShuffleAi } from 'base/ui/icons';
import { FormInput } from 'base/ui/input';
import { TextRegular, TextSmallLight, TextTinyLight } from 'base/ui/typography';
import { useDebounce } from 'base/useDebounce';
import classNames from 'classnames';
import clamp from 'lodash/clamp';
import { observer } from 'mobx-react-lite';
import { BespokePill } from './bespoke_pill';
import styles from './colors_editor.css';
import { getGlobalConceptsStore } from './concept_store';
import { ColorPickerPanel, copyColorToClipboard } from './ui';

/** The panel shown when editing colors on the styleguide. */
export const ColorsEditorPanel = observer(function _ColorsEditorPanel({
  analytics,
  navigation,
  toastQueue,
}: {
  analytics: AnalyticsService;
  navigation: SidebarNavigation;
  toastQueue: ToastQueue;
}) {
  const trackPaletteHexUpdated = useDebounce(
    (index: number, source: 'text' | 'visual') => {
      analytics.track({
        event: 'project:color_hex_update',
        palette: 'chromatic',
        chromatic: index + 1,
        source,
      });
    },
    1000,
  );
  const trackColorNameUpdated = useDebounce(
    (index: number) =>
      analytics.track({
        event: 'project:color_name_update',
        palette: 'chromatic',
        chromatic: index + 1,
      }),
    1000,
  );

  const { at } = navigation;
  if (!at) return null;

  // Default to show the primary palette editor if no palette is specified.
  const paletteRoute = at.path.split('/')[1] ?? 'chromatic1';
  const paletteName = assertPaletteName(paletteRoute);
  const conceptStore = getGlobalConceptsStore();
  const concept = conceptStore.activeConcept;
  let onShuffle;
  let updatePaletteName;
  let updatePaletteHex;
  let palette: CompiledNeutralColor | CompiledChromaticColor;

  if (paletteName === 'neutral') {
    updatePaletteName = undefined;
    updatePaletteHex = (hex: string) =>
      concept.updateNeutralTint(
        concept.chromatics.findIndex((chromatics) => chromatics.hex === hex),
      );
    onShuffle = undefined;
    palette = concept.neutral;
  } else if (paletteName.startsWith('chromatic')) {
    const index = clamp(
      Number(/(\d+)$/.exec(paletteName)![1]!) - 1,
      0,
      concept.chromatics.length - 1,
    );
    updatePaletteName = (name: string) => {
      concept.updateChromaticName(index, name);
      trackColorNameUpdated(index);
    };

    updatePaletteHex = (hex: string, source: 'text' | 'visual') => {
      concept.updateChromaticHex(index, hex);
      trackPaletteHexUpdated(index, source);
    };
    onShuffle = () => {
      concept.shuffleChromatic(index);
      analytics.track({
        event: 'project:shuffle_color_button_click',
        palette: 'chromatic',
        chromatic: index + 1,
        location: 'color_editor',
      });
    };
    palette = concept.chromatics[index];
  } else {
    throw new Error(`Invalid path for colors editor: ${at}`);
  }

  return (
    <EditPaletteTab
      analytics={analytics}
      navigation={navigation}
      toastQueue={toastQueue}
      palette={palette}
      onShuffle={onShuffle}
      updatePaletteHex={updatePaletteHex}
      updatePaletteName={updatePaletteName}
    />
  );
});

export const ShadesView = observer(function _ShadesView({
  analytics,
  palette,
  toastQueue,
  updatePaletteName,
  updatePaletteHex,
}: {
  analytics: AnalyticsService;
  palette: CompiledNeutralColor | CompiledChromaticColor;
  toastQueue: ToastQueue;
  updatePaletteName?: (name: string) => void;
  updatePaletteHex: (hex: string, source: 'text' | 'visual') => void;
}) {
  const isPaletteChromatic = palette.palette === 'chromatic';
  const [hexTextfieldValue, setHexTextfieldValue] = React.useState<string>(
    palette.hex,
  );
  const processInput = (value: string) => {
    setHexTextfieldValue(value);
    if (isValidHex(value)) {
      updatePaletteHex(value.startsWith('#') ? value : `#${value}`, 'text');
    }
  };
  const processColorInput = (value: string) => {
    setHexTextfieldValue(value);
    updatePaletteHex(value, 'visual');
  };
  const handleCopyHex = (e: React.MouseEvent) => {
    copyColorToClipboard(toastQueue, {
      color: palette.hex,
      name: palette.palette === 'chromatic' ? palette.name : 'Neutral',
    });
    analytics.track({
      event: 'project:copy_color_button_click',
      palette: palette.palette,
      chromatic: palette.chromatic,
      shade: 4,
      location: 'color_editor',
      hex: palette.hex,
    });
  };
  const colorForm = isPaletteChromatic ? (
    <div className={styles.colorForm}>
      <TextTinyLight color="grey">Hex</TextTinyLight>
      <FormInput
        invalid={!isValidHex(hexTextfieldValue)}
        value={hexTextfieldValue}
        type={'text'}
        onChange={(value) => {
          processInput(value);
        }}
        buttonEnd={
          <Button
            size="xsmall"
            variant="tertiary"
            background="grey2"
            onClick={handleCopyHex}
            label="Copy"
          />
        }
      />
      <TextTinyLight color="grey">Name</TextTinyLight>
      <FormInput
        onChange={updatePaletteName}
        value={palette.name}
        type="text"
      ></FormInput>
    </div>
  ) : null;

  return (
    <Rows spacing={16}>
      {isPaletteChromatic && (
        <>
          <ColorPickerPanel color={palette.hex} onChange={processColorInput} />
          {colorForm}
        </>
      )}

      <DividerLine></DividerLine>

      <TextRegular>Shades</TextRegular>
      <Rows spacing={8}>
        <TextTinyLight color="grey">Pastel</TextTinyLight>
        <PaletteShadeView
          analytics={analytics}
          palette={palette}
          shadeIndex={0}
          toastQueue={toastQueue}
        />
        <PaletteShadeView
          analytics={analytics}
          palette={palette}
          shadeIndex={1}
          toastQueue={toastQueue}
        />
      </Rows>
      <Rows spacing={8}>
        <TextTinyLight color="grey">Mid-Tone</TextTinyLight>
        <PaletteShadeView
          analytics={analytics}
          palette={palette}
          shadeIndex={2}
          toastQueue={toastQueue}
        />
        <PaletteShadeView
          analytics={analytics}
          palette={palette}
          shadeIndex={3}
          toastQueue={toastQueue}
        />
        <PaletteShadeView
          analytics={analytics}
          palette={palette}
          shadeIndex={4}
          toastQueue={toastQueue}
        />
      </Rows>
      <Rows spacing={8}>
        <TextTinyLight color="grey">Deep</TextTinyLight>
        <PaletteShadeView
          analytics={analytics}
          palette={palette}
          shadeIndex={5}
          toastQueue={toastQueue}
        />
        <PaletteShadeView
          analytics={analytics}
          palette={palette}
          shadeIndex={6}
          toastQueue={toastQueue}
        />
      </Rows>
    </Rows>
  );
});

const TintSection = observer(
  ({ analytics }: { analytics: AnalyticsService }) => {
    const concept = getGlobalConceptsStore().activeConcept;
    const tintIndex =
      concept.state.neutral.tintIndex != null &&
      concept.state.neutral.tintIndex < concept.chromatics.length
        ? concept.state.neutral.tintIndex
        : undefined;

    return (
      <>
        <Rows spacing={12}>
          <Rows spacing={4}>
            <TextRegular>Tint</TextRegular>
            <TextTinyLight color="grey">
              Apply a tint to your neutral shades using one of your brand
              colors.
            </TextTinyLight>
          </Rows>
          <Rows spacing={8}>
            {concept.chromatics.map(({ hex }, index) => (
              <ColorTintRow
                key={hex}
                tintHex={hex}
                active={index === tintIndex}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  concept.updateNeutralTint(index);
                  analytics.track({
                    event: 'project:update_neutral_tint_button_click',
                    palette: 'neutral',
                    chromatic: index + 1,
                  });
                }}
              />
            ))}
            <ColorTintRow
              tintHex={undefined}
              active={tintIndex == null}
              onClick={() => {
                concept.updateNeutralTint(undefined);
                analytics.track({
                  event: 'project:update_neutral_tint_button_click',
                  palette: 'neutral',
                  chromatic: undefined,
                });
              }}
            />
          </Rows>
        </Rows>
      </>
    );
  },
);

const ColorTintRow = ({
  tintHex,
  active,
  onClick,
}: {
  tintHex?: string;
  active: boolean;
  onClick: (e: React.MouseEvent) => void;
}) => {
  const neutralHex = getNeutralHex(tintHex, 0.1);
  return (
    <div
      className={classNames(styles.tintRow, active && styles.active)}
      onClick={onClick}
    >
      <div className={styles.colorSquare} style={{ background: tintHex }}>
        {tintHex == null && (
          <Icon size={14} color="colorBlack" icon={IconNoColor} />
        )}
      </div>
      <div className={styles.shades}>
        {generateNeutralShades(neutralHex)
          .slice(1, 7)
          .map((shade) => (
            <div key={shade} style={{ background: shade }} />
          ))}
      </div>
    </div>
  );
};

/**
 * Tab shown when editing a palette.
 */
export const EditPaletteTab = observer(function _EditColorTab({
  analytics,
  navigation,
  toastQueue,
  palette,
  onShuffle,
  updatePaletteName,
  updatePaletteHex,
}: {
  analytics: AnalyticsService;
  navigation: SidebarNavigation;
  toastQueue: ToastQueue;
  palette: CompiledNeutralColor | CompiledChromaticColor;
  onShuffle?: () => void;
  updatePaletteName?: (name: string) => void;
  updatePaletteHex: (hex: string, source: 'text' | 'visual') => void;
}) {
  const isPaletteChromatic = palette.palette === 'chromatic';
  return (
    <PanelLayout
      title={isPaletteChromatic ? 'Colors' : 'Neutrals'}
      navigation={navigation}
      footer={
        onShuffle != null ? (
          <Rows spacing={16}>
            <Button
              fullWidth
              variant="secondary"
              label="Shuffle"
              icon={IconShuffleAi}
              iconColor="ai"
              onClick={onShuffle}
            />
          </Rows>
        ) : undefined
      }
    >
      <Rows spacing={16}>
        {!isPaletteChromatic && <TintSection analytics={analytics} />}
        <ShadesView
          analytics={analytics}
          palette={palette}
          toastQueue={toastQueue}
          updatePaletteName={updatePaletteName}
          updatePaletteHex={updatePaletteHex}
        />
      </Rows>
    </PanelLayout>
  );
});

export function PaletteShadeView({
  analytics,
  palette,
  shadeIndex,
  toastQueue,
}: {
  analytics: AnalyticsService;
  palette: CompiledNeutralColor | CompiledChromaticColor;
  shadeIndex: number;
  toastQueue: ToastQueue;
}) {
  const isPaletteChromatic = palette.palette === 'chromatic';
  const offset = !isPaletteChromatic ? 1 : 0;
  const shade = palette.shades[shadeIndex + offset];
  const shadeNames = [
    'Lightest',
    'Lighter',
    'Light',
    '',
    'Dark',
    'Darker',
    'Darkest',
  ];
  const shadeName = shadeNames[shadeIndex];
  const isPaletteSource = isPaletteChromatic && shade === palette.hex;

  const name = isPaletteChromatic ? palette.name : 'Neutral';
  let colorText = shadeName === '' ? name : `${name} • ${shadeName}`;

  return (
    <div
      className={classNames(
        styles.paletteShade,
        palette.locked && styles.locked,
      )}
    >
      <Inline yalign="center" spacing={8}>
        <div className={styles.swatch} style={{ backgroundColor: shade }}>
          {isPaletteSource && <div className={styles.dot} />}
        </div>
        <div className={styles.shadeHex}>
          <TextSmallLight lineClamp={1}>
            {shade.slice(1).toUpperCase()}
          </TextSmallLight>
        </div>
        <div className={styles.shadeName}>
          <TextSmallLight lineClamp={1}>{colorText}</TextSmallLight>
        </div>
      </Inline>
      <Inline spacing={4}>
        <div className={styles.copyButton}>
          <Button
            size="xsmall"
            variant="tertiary"
            background="grey2"
            onClick={(e) => {
              e.preventDefault();
              copyColorToClipboard(toastQueue, {
                color: shade,
                name: colorText,
              });
              analytics.track({
                event: 'project:copy_color_button_click',
                palette: palette.palette,
                chromatic: palette.chromatic,
                shade: shadeIndex + offset,
                location: 'shades_view',
                hex: shade,
              });
            }}
            label="Copy"
          />
        </div>
        {isPaletteSource && <BespokePill label="Base" />}
      </Inline>
    </div>
  );
}

function isValidHex(hex: string) {
  return /^#?([a-f0-9]{6})$/.test(hex.toLowerCase());
}
